import React, { useEffect, useMemo, useState } from 'react';

import { CircularProgress, MenuItem, Select, Tooltip } from '@material-ui/core';
import { PLACEHOLDER } from 'constants/index';
import { useStudentPerformanceTrends, useTeacherSections } from 'sdk';
import { cutText } from 'utils';

import { ReactComponent as InformationIcon } from 'pages/Teachers/Home/assets/information-icon.svg';
import { ReactComponent as TrendsIcon } from 'pages/Teachers/Home/assets/trends-icon.svg';
import ClassPerformanceTrendsChart from 'pages/Teachers/Home/components/ClassPerformanceTrendsChart';
import { ClassPerformanceTrendsPanel } from 'pages/Teachers/Home/components/ClassPerformanceTrendsPanel';
import { useEdLightTracking } from 'utils/integrations/mixpanel';

import styles from './styles.module.scss';

const ClassPerformanceTrends = () => {
  const [sectionId, setSectionId] = useState(PLACEHOLDER);
  const [activeWeekIndex, setActiveWeekIndex] = useState(0);

  const { data: sections } = useTeacherSections();
  const { data: rawTrends, isLoading } = useStudentPerformanceTrends({
    sectionId
  });

  const { trackEvent } = useEdLightTracking({
    feature: 'class-performance-trends',
    view: 'home'
  });

  const performanceTrends = useMemo(() => {
    if (Array.isArray(rawTrends)) return rawTrends;
    return rawTrends?.trends || [];
  }, [rawTrends]);

  const safeActiveWeekIndex = Math.min(
    activeWeekIndex,
    Math.max(0, performanceTrends.length - 1)
  );

  useEffect(() => {
    if (performanceTrends.length >= 1 && !isLoading) {
      setActiveWeekIndex(performanceTrends.length - 1);
    }
  }, [performanceTrends, isLoading]);

  const hasSelectedClass = sectionId && sectionId !== PLACEHOLDER;

  const handleSelectWeek = ({ index, data }) => {
    setActiveWeekIndex(index);

    trackEvent('week-selected', {
      assignment_count: data?.assignment_count,
      mastery_percentages: data?.mastery_percentages,
      standards: data?.standards,
      student_count: data?.student_count,
      upload_percentage: data?.upload_percentage,
      week_start: data?.week_start,
      week_end: data?.week_end
    });
  };

  const handleSelectSection = (selectedId) => {
    setSectionId(selectedId);
    setActiveWeekIndex(0);

    if (selectedId && selectedId !== PLACEHOLDER) {
      trackEvent('section-selected', {
        section_id: selectedId
      });
    }
  };

  const activeTrend = performanceTrends?.[safeActiveWeekIndex];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>
              <TrendsIcon className={styles.trendsIcon} />
              Class Performance Trends
            </h3>
            <Tooltip
              title="Click any point to see what changed and view related assignments."
              placement="right"
              arrow
              classes={{ tooltip: styles.tooltip }}
            >
              <span className={styles.infoIconWrapper}>
                <InformationIcon />
              </span>
            </Tooltip>
          </div>
          <p className={styles.subtitle}>
            See how student's performance &amp; mastery levels changes across
            Celebrate, Support and Intervene over time.
          </p>
        </div>

        <div className={styles.selectWrapper}>
          <Select
            value={sectionId}
            onChange={(e) => handleSelectSection(e.target.value)}
            className={styles.sectionSelect}
            MenuProps={{
              PaperProps: {
                style: {
                  maxWidth: 280
                }
              }
            }}
            displayEmpty
            disableUnderline
          >
            <MenuItem value="placeholder" disabled>
              Select Class Name
            </MenuItem>
            {sections?.map((section) => (
              <MenuItem key={`section-${section.id}`} value={section.id}>
                {cutText({ text: section.name, char: 31 })}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>

      <div className={styles.content}>
        {!hasSelectedClass ? (
          <div className={styles.selectPromptState}>
            <div className={styles.promptIconBadge}>
              <TrendsIcon className={styles.promptIcon} />
            </div>
            <h4 className={styles.promptTitle}>Select a Class</h4>
            <p className={styles.promptSubtitle}>
              Please select a class section from the dropdown above to view
              performance trends and mastery levels.
            </p>
          </div>
        ) : isLoading ? (
          <div className={styles.loadingWrapper}>
            <CircularProgress size={36} className={styles.spinner} />
            <span className={styles.loadingText}>Loading trends data...</span>
          </div>
        ) : (
          <>
            <div className={styles.graphContainer}>
              <ClassPerformanceTrendsChart
                trends={performanceTrends}
                activeWeekIndex={activeWeekIndex}
                onSelectWeek={handleSelectWeek}
              />
            </div>

            <ClassPerformanceTrendsPanel activeTrend={activeTrend} />
          </>
        )}
      </div>
    </div>
  );
};

export default ClassPerformanceTrends;
